import { CheckBox, Modal, Input, InputDropdown, InputDropdownItem } from '@platform/components';
import { AdminIntroductions } from '@platform/client';
import { DeclineReasons } from '../../../dashboard/my-projects/Modals/IntroductionModal';
import { IntroductionOutcomeEnum, AdminIntroduction } from '@platform/client/schemas';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { snakeToTitleCase } from '../../../util/TitleCase';

type IntroductionOutcomeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  introduction: AdminIntroduction;
  page?: number;
  limit?: number;
};

const IntroductionOutcomeModal = (props: IntroductionOutcomeModalProps) => {
  const { isOpen, onClose, introduction, page = 0, limit = 20 } = props;
  const [outcome, setOutcome] = useState<IntroductionOutcomeEnum>();
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [declineReason, setDeclineReason] = useState<DeclineReasons>();
  const [otherReasonText, setOtherReasonText] = useState<string>();

  const dropownItemsOutcomes: InputDropdownItem[] = Object.values(IntroductionOutcomeEnum).map(
    (outcome) => ({
      text: snakeToTitleCase(outcome),
      textValue: outcome,
    }),
  );

  const dropdownItemsReasons: InputDropdownItem[] = Object.entries(DeclineReasons).map(
    ([reason, id]) => ({
      text: snakeToTitleCase(reason),
      textValue: id,
    }),
  );

  const onOutcomeSelectChange = (key: React.Key) => {
    setDeclineReason(undefined);
    setOutcome(key as IntroductionOutcomeEnum);
  };
  const onModalClose = () => {
    setDeclineReason(undefined);
    setOutcome(undefined);
    setSendEmail(false);
    onClose();
  };

  const queryClient = useQueryClient();
  const updateOutcome = AdminIntroductions.useUpdateIntroductionOutcome({
    mutation: {
      onMutate: () => {
        onModalClose();
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminIntroductions.getGetIntroductionsQueryKey({
              page: page,
              limit: limit,
            }),
          ],
        });
      },
    },
  });

  return (
    <Modal
      onClose={onModalClose}
      isOpen={isOpen}
      headerDivision='divider'
      title={`Update Introduction (${introduction?.introductionId}) Outcome`}
      variant='dialog'
      primaryCTA={{
        text: 'Update Outcome',
        callback: () => {
          if (introduction && outcome) {
            updateOutcome.mutate({
              data: {
                introductionId: introduction.introductionId,
                outcome: outcome,
                sendEmail: sendEmail,
                reasonId: Number(declineReason),
              },
            });
          }
          onModalClose();
        },
        isDisabled: () => !outcome || (outcome === 'declined' && !declineReason),
      }}
      secondaryCTA={{
        text: 'Cancel',
        callback: onModalClose,
      }}
      ignoreExtraPadding
    >
      <div className='flex flex-col gap-4 w-[450px]'>
        <div className='flex flex-col gap-2'>
          <span className='text-base font-semibold'>Professional</span>
          <span className='text-base text-gray-500'>{introduction?.experteerName}</span>
          <span className='text-base font-semibold'>Representative</span>
          <span className='text-base text-gray-500'>{introduction?.representativeName}</span>
          <span className='text-base font-semibold'>Organization</span>
          <span className='text-base text-gray-500'>{introduction?.organizationName}</span>
          <span className='text-base font-semibold'>Project</span>
          <span className='text-base text-gray-500'>
            {introduction?.projectName ?? 'No Project'}
          </span>
          <span className='text-base font-semibold'>Current Outcome</span>
          <span className='text-base text-gray-500'>{introduction?.outcome ?? ''}</span>
        </div>
        <div className='flex flex-col gap-2'>
          <span className='text-base font-semibold'>New Outcome</span>
          <InputDropdown
            aria-label='introduction outcome'
            type='default'
            items={dropownItemsOutcomes}
            inputDisabled
            onSelectionChange={(key) => onOutcomeSelectChange(key as IntroductionOutcomeEnum)}
          ></InputDropdown>
          {outcome === 'declined' && (
            <>
              <span className='text-base font-semibold'>Decline Reason</span>
              <InputDropdown
                aria-label='declined introduction reasons'
                type='default'
                items={dropdownItemsReasons}
                inputDisabled
                onSelectionChange={(key) => setDeclineReason(key as DeclineReasons)}
              ></InputDropdown>
            </>
          )}
          {outcome === 'declined' && declineReason === DeclineReasons.Other && (
            <Input
              type='multiline'
              value={otherReasonText as string}
              aria-label='additional details'
              placeholder='Additional details'
              onChange={(e) => {
                setOtherReasonText(e.target.value);
              }}
            ></Input>
          )}
        </div>
        <CheckBox
          aria-label='send email'
          description='Trigger an email notification based on this outcome'
          size='md'
          onChange={(value) => {
            setSendEmail(value);
          }}
        >
          Send Email
        </CheckBox>
      </div>
    </Modal>
  );
};

export default IntroductionOutcomeModal;
